import { ReactNode, createContext, useMemo, useState } from 'react'

import { IconLabelToggle } from '@joor/design-system'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { OrderStatus } from '__generated__/atlas-types'

import { useUserAnalytics } from 'utils/sdks/userAnalytics'

import {
  isAccountTypeBrand,
  isEditApprovedShippedRestricted,
} from 'store/currentUser/selectors'

import { OrderData } from '../Overview/Overview'
import withModal from 'containers/ModalHandler/ModalHandler.component'
import AddProductsModalContainer from 'features/Orders/Review/modals/AddProductsModal/AddProductsModal.container'
import { useRedirectToCatalog } from 'features/Orders/Review/review.hooks'
import { useOrderId } from 'features/Orders/orders.hooks'

const IconLabelToggleWrapper = styled.div`
  width: 145px;
`

export const AddProductsButtonTestId = 'add-products-toggle'

type AddProductsButtonType = {
  order?: {
    collections: OrderData['collections']['edges']
    brand: OrderData['brand']
    priceType: OrderData['priceType']
    status: OrderData['status']
  }
  openDynamicModal: (value?: unknown) => void
  closeDynamicModal: () => void
  onCloseModal?: () => void
  children?: ReactNode
  id?: string
}

export const AddProductsContext = createContext({
  handleAddProducts: () => {},
})

const AddProductsButton = ({
  // TODO: this button should be removed. It's only used in AddProductsNonVisible
  // I have duplicated and created AddProductsLink which should be the main component
  // This is a deprecated one only existing so I don't have to deal with the new linkbutton working properly
  // in AddProductsNonVisible atm
  id,
  order,
  openDynamicModal,
  closeDynamicModal,
  onCloseModal,
  children,
}: AddProductsButtonType) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const isBrandAccount = useSelector(isAccountTypeBrand)
  const isRestrictEditApprovedShipped = useSelector(
    isEditApprovedShippedRestricted,
  )
  const orderId = useOrderId()
  const redirectToCatalog = useRedirectToCatalog()
  const { trackEvent } = useUserAnalytics()

  const isButtonDisabled = useMemo(() => {
    if (isBrandAccount) {
      return (
        (isRestrictEditApprovedShipped &&
          (order?.status === OrderStatus.APPROVED ||
            order?.status === OrderStatus.SHIPPED)) ||
        order?.status === OrderStatus.CANCELLED
      )
    }

    const hasNotAllowedOrderStatusForLiteRetailer = !(
      order?.status === OrderStatus.IN_PROGRESS ||
      order?.status === OrderStatus.NOTES ||
      order?.status === OrderStatus.ALL_NOTES ||
      order?.status === OrderStatus.RETURNED
    )

    return (
      hasNotAllowedOrderStatusForLiteRetailer ||
      order.collections.every(
        (collection) =>
          collection?.node.archived || !collection?.node.visibleToViewer,
      )
    )
  }, [
    isBrandAccount,
    isRestrictEditApprovedShipped,
    order?.collections,
    order?.status,
  ])

  const openAddProductsModal = () => {
    setIsModalOpen(true)
    openDynamicModal({
      component: AddProductsModalContainer,
      orderId,
      brandId: order?.brand.id,
      priceTypeId: order?.priceType?.id,
      onClose: () => {
        setIsModalOpen(false)
        closeDynamicModal()
        onCloseModal?.()
      },
    })
  }

  const handleAddProducts = () => {
    switch (order?.status) {
      case OrderStatus.APPROVED:
      case OrderStatus.SHIPPED:
        if (isBrandAccount && !isRestrictEditApprovedShipped) {
          openAddProductsModal()
        }
        break
      case OrderStatus.PENDING:
        openAddProductsModal()
        break
      case OrderStatus.NOTES:
        openAddProductsModal()
        break
      case OrderStatus.IN_PROGRESS:
        if (isBrandAccount) {
          redirectToCatalog()
        } else {
          openAddProductsModal()
        }
        break
    }
    trackEvent('Order_Review_Add_Products_Button')
  }

  if (children) {
    return (
      <AddProductsContext.Provider value={{ handleAddProducts }}>
        {children}
      </AddProductsContext.Provider>
    )
  }

  return (
    <IconLabelToggleWrapper>
      <IconLabelToggle
        id={id}
        iconName="add"
        label="Add Products"
        onToggle={handleAddProducts}
        variant={isButtonDisabled ? 'DISABLED' : 'DEFAULT'}
        data-testid={AddProductsButtonTestId}
        checked={isModalOpen}
      />
    </IconLabelToggleWrapper>
  )
}

export default withModal(AddProductsButton)
